import { zodResolver } from '@hookform/resolvers/zod';

/**
 * How long an address line may be.
 *
 * Mirrors the server's `ADDRESS_MAX_LENGTH`, which is not a round number picked
 * for looks: the column holds *ciphertext*, roughly a third longer than the text
 * plus a fixed header, and 150 characters is the most that still fits.
 */
export const ADDRESS_MAX_LENGTH = 150;
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';

import { INDIAN_STATES } from '@/constants/institute';
import { SELECTABLE_ADMISSION_TYPES } from '@/features/institute/admissions/constants';
// The cap on a class's yearly fee is the cap on what one student can be
// charged for it — one number, not two that can drift apart.
import { MAX_FEE } from '@/features/institute/classes/schemas/class.schema';

/**
 * What an admission must look like before the office can register it.
 *
 * These rules are for fast feedback only. When the students module lands, the
 * server validates every one of them again and stays the authority — a client
 * check is a courtesy to whoever is typing, never a guarantee (CLAUDE.md §6).
 *
 * Every field arrives as the string an `<input>` holds and leaves in the shape
 * the API takes: money as numbers, blanks as `undefined` rather than `''`.
 */

/** Indian names carry initials, apostrophes and hyphens: "D'Souza", "Jean-Paul". */
const NAME_PATTERN = /^[\p{L} .'-]+$/u;
const NAME_MESSAGE = "Letters, spaces and . ' - only";
/** Indian mobile numbers are ten digits and never begin below 6. */
const MOBILE_PATTERN = /^[6-9]\d{9}$/;
/** Aadhaar is twelve digits and never begins 0 or 1. */
const AADHAAR_PATTERN = /^[2-9]\d{11}$/;

const requiredName = (label: string, max = 60) =>
  z
    .string()
    .trim()
    .min(2, `${label} is too short`)
    .max(max, `${label} is too long`)
    .regex(NAME_PATTERN, NAME_MESSAGE);

const optionalName = (max = 60) =>
  z
    .string()
    .trim()
    .max(max, 'That name is too long')
    .regex(NAME_PATTERN, NAME_MESSAGE)
    .or(z.literal(''))
    .transform((value) => value || undefined);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'That is too long')
    .optional()
    .transform((value) => value || undefined);

/** Spaces and hyphens are how people write a number down; strip, then check. */
const optionalMobile = z
  .string()
  .trim()
  .transform((value) => value.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(MOBILE_PATTERN, 'Enter a valid 10-digit mobile number').or(z.literal('')))
  .transform((value) => value || undefined);

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .email('Enter a valid email address')
  .max(150, 'That address is too long')
  .or(z.literal(''))
  .transform((value) => value || undefined);

/** `<input type="date">` gives us `YYYY-MM-DD` or an empty string. */
const isoDate = (message: string) => z.string().regex(/^\d{4}-\d{2}-\d{2}$/, message);

/**
 * Money the office types in, in whole rupees. Paise are never entered by hand;
 * they arrive from a gateway, so an input that accepts them only invites a slip
 * that a parent later disputes.
 */
const amount = z
  .string()
  .trim()
  .min(1, 'Enter an amount')
  .regex(/^\d+$/, 'Whole rupees only')
  .refine((value) => Number(value) <= MAX_FEE, 'That looks too high — check the amount')
  .transform(Number);

/**
 * A transfer must name where the child came from and the TC they brought.
 * Mirrors the server; every other admission type leaves both optional.
 *
 * Its own schema, joined to the rest with `.and()`, rather than one more check
 * in the form-wide `superRefine`: zod skips that refinement while any field
 * with a transform is still invalid — a blank father's mobile is enough — so
 * these two errors stayed hidden until the rest of the form was already right.
 */
const transferRule = z
  .object({
    admissionType: z.enum(SELECTABLE_ADMISSION_TYPES),
    previousSchool: optionalText(150),
    transferCertificateNumber: optionalText(40),
  })
  .superRefine((values, ctx) => {
    if (values.admissionType !== 'TRANSFER') return;

    if (!values.previousSchool) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['previousSchool'],
        message: 'Needed for a transfer — name the institute the child is leaving',
      });
    }
    if (!values.transferCertificateNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['transferCertificateNumber'],
        message: 'Needed for a transfer — enter the TC number',
      });
    }
  });

const GENDERS = ['MALE', 'FEMALE', 'OTHER'] as const;
const CATEGORIES = ['GENERAL', 'OBC', 'SC', 'ST', 'EWS'] as const;

const admissionDetails = z
  .object({
    // --- A. the child ------------------------------------------------------
    firstName: requiredName('The first name'),
    middleName: optionalName(),
    lastName: requiredName('The last name'),
    /**
     * The child's own address, and the only thing that opens a student login.
     * Optional because most school children have none — a school forced to
     * invent one to admit somebody would invent one.
     */
    email: optionalEmail,
    // Blank in, the enum out: the field starts empty so the office chooses,
    // rather than every child nobody corrected being admitted as a boy. Blank
    // fails the second enum with the message below.
    gender: z
      .enum(GENDERS)
      .or(z.literal(''))
      .pipe(z.enum(GENDERS, { errorMap: () => ({ message: 'Choose a gender' }) })),
    dateOfBirth: isoDate('A date of birth is required'),
    bloodGroup: z.enum(['UNKNOWN', 'A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']),
    religion: optionalText(40),
    // Starts empty for the same reason as `gender`: a category decides which
    // certificate is asked for, and a default would claim one nobody chose.
    category: z
      .enum(CATEGORIES)
      .or(z.literal(''))
      .pipe(z.enum(CATEGORIES, { errorMap: () => ({ message: 'Choose a category' }) })),
    nationality: z.string().trim().min(2, 'Enter the nationality').max(40, 'That is too long'),
    aadhaarNumber: z
      .string()
      .trim()
      .transform((value) => value.replace(/\s/g, ''))
      .pipe(z.string().regex(AADHAAR_PATTERN, 'Aadhaar is 12 digits').or(z.literal('')))
      .transform((value) => value || undefined),

    // --- B. placement ------------------------------------------------------
    classUuid: z.string().min(1, 'Choose a class'),
    sectionUuid: z.string().min(1, 'Choose a section'),
    admissionDate: isoDate('An admission date is required'),
    // `admissionType`, `previousSchool` and `transferCertificateNumber` live in
    // `transferRule` below.
    previousClass: optionalText(40),

    // --- C. parents and guardian -------------------------------------------
    // No single parent is required — only the first contact, below.
    fatherName: optionalName(),
    fatherOccupation: optionalText(60),
    fatherMobile: optionalMobile,
    fatherEmail: optionalEmail,
    motherName: optionalName(),
    motherOccupation: optionalText(60),
    motherMobile: optionalMobile,
    motherEmail: optionalEmail,
    guardianName: optionalName(),
    guardianRelationship: optionalText(40),
    guardianMobile: optionalMobile,
    primaryContact: z.enum(['FATHER', 'MOTHER', 'GUARDIAN']),

    // --- D. where the child lives ------------------------------------------
    addressLine1: z
      .string()
      .trim()
      .min(3, 'An address is required')
      .max(ADDRESS_MAX_LENGTH, 'That is too long'),
    addressLine2: optionalText(ADDRESS_MAX_LENGTH),
    city: z.string().trim().min(2, 'A city or town is required').max(80, 'That is too long'),
    district: optionalText(80),
    // A plain string rather than `z.enum`, so the field can start genuinely
    // empty: no institute's state is a sensible default for every other institute.
    state: z
      .string()
      .refine((value) => INDIAN_STATES.some((state) => state === value), 'Choose a state'),
    pincode: z
      .string()
      .trim()
      .regex(/^[1-9]\d{5}$/, 'A PIN code is 6 digits')
      .or(z.literal(''))
      .transform((value) => value || undefined),

    // --- E. transport ------------------------------------------------------
    usesTransport: z.boolean(),
    routeName: optionalText(80),
    busNumber: optionalText(30),
    pickupPoint: optionalText(80),

    // --- F. what is being charged ------------------------------------------
    tuitionFee: amount,
    tuitionDiscount: amount,
    tuitionPaid: amount,
    booksFee: amount,
    booksDiscount: amount,
    booksPaid: amount,
    otherFee: amount,
    otherDiscount: amount,
    otherPaid: amount,
    concessionReason: optionalText(120),
    /** Optional; blank leaves the family on the class's installment dates. */
    feeDueDate: isoDate('Use a date like 2026-08-15')
      .or(z.literal(''))
      .transform((value) => value || undefined),
  })
  .superRefine((values, ctx) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const born = new Date(values.dateOfBirth);
    const admitted = new Date(values.admissionDate);

    if (born > today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dateOfBirth'],
        message: 'A date of birth cannot be in the future',
      });
    } else if (born.getFullYear() < 1900) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dateOfBirth'],
        message: 'Check the year',
      });
    } else if (admitted < born) {
      // Caught here rather than on the birth date: the admission date is the
      // one that is nearly always the typo.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['admissionDate'],
        message: 'This is before the date of birth',
      });
    }

    /**
     * One address the student panel's invitation can be sent to.
     *
     * Mirrors the server, which decides (CLAUDE.md §6): a school child has no
     * inbox of their own, so the link goes to a parent's, and a record with no
     * address anywhere on it is a child who can never sign in. Either parent
     * will do, and the student's own address exempts both.
     */
    if (!values.email && !values.fatherEmail && !values.motherEmail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fatherEmail'],
        message: "Add the father's or mother's email — the student's login link is sent to it",
      });
    }

    // The institute rings one number first. Whichever it is must exist, named
    // and reachable — mirrors the server's `applyStudentRecordRules`.
    for (const parent of [
      { kind: 'FATHER', label: 'father', name: values.fatherName, mobile: values.fatherMobile },
      { kind: 'MOTHER', label: 'mother', name: values.motherName, mobile: values.motherMobile },
    ] as const) {
      if (values.primaryContact !== parent.kind) continue;
      if (!parent.name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`${parent.label}Name`],
          message: `Needed — the ${parent.label} is this student's first contact`,
        });
      }
      if (!parent.mobile) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`${parent.label}Mobile`],
          message: `Needed — the ${parent.label} is this student's first contact`,
        });
      }
    }

    // A number or address with nobody's name on it is dropped when the record
    // is written, and opens no parent login — so ask for the name instead.
    for (const parent of [
      { label: 'father', name: values.fatherName, mobile: values.fatherMobile, email: values.fatherEmail },
      { label: 'mother', name: values.motherName, mobile: values.motherMobile, email: values.motherEmail },
    ] as const) {
      if (!parent.name && (parent.mobile || parent.email)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`${parent.label}Name`],
          message: `Add the ${parent.label}'s name, or clear their number and email`,
        });
      }
    }
    if (values.primaryContact === 'GUARDIAN') {
      if (!values.guardianName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['guardianName'],
          message: "Needed — the guardian is this student's first contact",
        });
      }
      if (!values.guardianMobile) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['guardianMobile'],
          message: "Needed — the guardian is this student's first contact",
        });
      }
    }

    // A guardian nobody can reach is a record, not a contact.
    if (values.guardianName && !values.guardianMobile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['guardianMobile'],
        message: 'Add a number for the guardian',
      });
    }

    if (values.usesTransport) {
      if (!values.routeName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['routeName'],
          message: 'Name the route this child travels on',
        });
      }
      if (!values.pickupPoint) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pickupPoint'],
          message: 'Where is the child picked up?',
        });
      }
    }

    // Money that does not add up is caught before it reaches a ledger.
    const heads = [
      { fee: 'tuitionFee', discount: 'tuitionDiscount', paid: 'tuitionPaid' },
      { fee: 'booksFee', discount: 'booksDiscount', paid: 'booksPaid' },
      { fee: 'otherFee', discount: 'otherDiscount', paid: 'otherPaid' },
    ] as const;

    for (const head of heads) {
      const fee = values[head.fee];
      const discount = values[head.discount];
      const paid = values[head.paid];

      if (discount > fee) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [head.discount],
          message: 'A discount cannot be more than the fee',
        });
        continue;
      }
      if (paid > fee - discount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [head.paid],
          message: 'More than the amount payable',
        });
      }
    }
  });

export const admissionSchema = admissionDetails.and(transferRule);

/** What the form holds — every field a string, as the inputs give them to us. */
export type AdmissionFormValues = z.input<typeof admissionSchema>;
/** What a save sends on: money as numbers, blanks dropped. */
export type AdmissionPayload = z.output<typeof admissionSchema>;

/**
 * `raw` keeps the form state holding what the inputs hold. Without it the
 * submit handler is handed the *transformed* values — money already numbers —
 * while the form still holds strings, so the declared `AdmissionFormValues`
 * would be a lie. Same reasoning as the class form.
 */
export const admissionFormResolver: Resolver<AdmissionFormValues> = zodResolver(
  admissionSchema,
  undefined,
  { raw: true },
);
